import React, { useRef, useEffect, useCallback } from 'react';
import { Box, IconButton, Divider } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';

// ── Helpers (outside component to avoid re-creation) ───────────────────────

/**
 * Count plain-text characters (strips HTML tags and zero-width spaces).
 * @param {string} html
 * @returns {number}
 */
const countPlainChars = (html) => {
    if (typeof window !== 'undefined' && window.document) {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return (tmp.textContent ?? '').replace(/\u200B/g, '').length;
    }
    return html.replace(/<[^>]*>/g, '').length;
};

/**
 * @param {{
 *   value: string,
 *   onChange: (value: string) => void,
 *   placeholder?: string,
 *   minHeight?: string | number,
 *   maxHeight?: string | number,
 *   onBlur?: () => void,
 *   maxChars?: number,
 *   onMaxReached?: () => void,
 * }} props
 */
const CustomEditor = ({
    value,
    onChange,
    placeholder = 'Write something...',
    minHeight = 150,
    maxHeight = 250,
    onBlur,
    maxChars,
    onMaxReached,
}) => {
    const editorRef = useRef(null);

    // ── STABILITY REFS (Ensure handlers never change identity) ────────────────
    const isFocusedRef = useRef(false);
    const lastEmittedHtmlRef = useRef(value);
    const onChangeRef = useRef(onChange);
    const onMaxReachedRef = useRef(onMaxReached);
    const onBlurRef = useRef(onBlur);
    const maxCharsRef = useRef(maxChars);
    const valueRef = useRef(value);

    // Sync unstable props to refs on every render (without triggering re-renders)
    useEffect(() => {
        onChangeRef.current = onChange;
        onMaxReachedRef.current = onMaxReached;
        onBlurRef.current = onBlur;
        maxCharsRef.current = maxChars;
        valueRef.current = value;
    });

    // ── Initial / external value sync ────────────────────────────────────────
    // Only runs when the editor is NOT focused, so user edits are never clobbered.
    useEffect(() => {
        const el = editorRef.current;
        if (!el) return;
        if (isFocusedRef.current) return; // ← user is typing, skip sync
        if (el.innerHTML !== value) {
            el.innerHTML = value;
            lastEmittedHtmlRef.current = value; // track external update
        }
    }, [value]);

    // ── Focus tracking ────────────────────────────────────────────────────────
    const handleFocus = useCallback(() => {
        isFocusedRef.current = true;
    }, []);

    const handleBlur = useCallback(() => {
        isFocusedRef.current = false;
        onBlurRef.current?.();
    }, []);

    // ── Emit HTML to parent ───────────────────────────────────────────────────
    const emitChange = useCallback(() => {
        if (editorRef.current) {
            const html = editorRef.current.innerHTML;
            // ONLY emit if it's different from what we last sent to parent
            // This stops infinite state-update loops.
            if (html !== lastEmittedHtmlRef.current) {
                lastEmittedHtmlRef.current = html;
                onChangeRef.current(html);
            }
        }
    }, []);

    // ── Normal typing ─────────────────────────────────────────────────────────
    const handleInput = useCallback(() => {
        const el = editorRef.current;
        if (!el) return;

        // Enforce maxChars: if exceeded, undo the last input
        if (maxCharsRef.current !== undefined) {
            const currentCount = countPlainChars(el.innerHTML);
            if (currentCount > maxCharsRef.current) {
                // Restore previous content from value prop (last accepted state)
                const sel = window.getSelection();
                const range = sel?.rangeCount ? sel.getRangeAt(0) : null;
                el.innerHTML = valueRef.current; // revert to last good value
                // Try to restore caret to end
                if (range && el.lastChild) {
                    try {
                        const newRange = document.createRange();
                        newRange.selectNodeContents(el);
                        newRange.collapse(false);
                        sel?.removeAllRanges();
                        sel?.addRange(newRange);
                    } catch { }
                }
                onMaxReachedRef.current?.();
                return;
            }
        }

        emitChange();
    }, [emitChange]);

    // ── Toolbar commands ──────────────────────────────────────────────────────
    const preventFocusLoss = useCallback((e) => {
        e.preventDefault();
    }, []);

    const handleExecCommand = useCallback((command) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        document.execCommand(command, false);
        emitChange();
    }, [emitChange]);

    // ── Key handling ──────────────────────────────────────────────────────────
    const handleKeyDown = useCallback((e) => {
        // Keyboard shortcuts (Ctrl/Cmd + B, I, U)
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
            const key = e.key.toLowerCase();
            if (key === 'b') {
                e.preventDefault();
                handleExecCommand('bold');
            } else if (key === 'i') {
                e.preventDefault();
                handleExecCommand('italic');
            } else if (key === 'u') {
                e.preventDefault();
                handleExecCommand('underline');
            }
        }

        // Shift+Enter → <br> soft line-break
        if (e.key === 'Enter' && e.shiftKey) {
            e.preventDefault();
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0) return;
            const range = sel.getRangeAt(0);
            const br = document.createElement('br');
            range.deleteContents();
            range.insertNode(br);
            range.setStartAfter(br);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
            emitChange();
            return;
        }

        // Emoji-safe backspace (emoji are 2 code-units wide)
        if (e.key === 'Backspace') {
            const sel = window.getSelection();
            if (!sel?.anchorNode) return;
            const node = sel.anchorNode;
            if (node.nodeType !== Node.TEXT_NODE) return;
            const text = node.textContent ?? '';
            const offset = sel.anchorOffset;
            if (offset < 2) return;
            if (/\p{Extended_Pictographic}/u.test(text.slice(offset - 2, offset))) {
                e.preventDefault();
                node.textContent = text.slice(0, offset - 2) + text.slice(offset);
                const range = document.createRange();
                range.setStart(node, offset - 2);
                range.collapse(true);
                sel.removeAllRanges();
                sel.addRange(range);
                emitChange();
            }
        }
    }, [emitChange, handleExecCommand]);

    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text);
        emitChange();
    }, [emitChange]);

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <Box
            sx={{
                border: '1px solid #E5E5E5',
                borderRadius: '10px',
                bgcolor: '#FFF',
                overflow: 'hidden',
                width: '100%',
                maxWidth: '100%',
            }}
        >
            {/* ── Toolbar ── */}
            <Box
                sx={{
                    px: 1.5,
                    py: 0.8,
                    bgcolor: '#F8F8F8',
                    borderBottom: '1px solid #E5E5E5',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                }}
            >
                <IconButton
                    size="small"
                    title="Bold"
                    onMouseDown={(e) => { preventFocusLoss(e); handleExecCommand('bold'); }}
                >
                    <FormatBoldIcon fontSize="small" />
                </IconButton>

                <IconButton
                    size="small"
                    title="Italic"
                    onMouseDown={(e) => { preventFocusLoss(e); handleExecCommand('italic'); }}
                >
                    <FormatItalicIcon fontSize="small" />
                </IconButton>

                <Divider orientation="vertical" flexItem />

                <IconButton
                    size="small"
                    title="Bullet list"
                    onMouseDown={(e) => { preventFocusLoss(e); handleExecCommand('insertUnorderedList'); }}
                >
                    <FormatListBulletedIcon fontSize="small" />
                </IconButton>

                <IconButton
                    size="small"
                    title="Numbered list"
                    onMouseDown={(e) => { preventFocusLoss(e); handleExecCommand('insertOrderedList'); }}
                >
                    <FormatListNumberedIcon fontSize="small" />
                </IconButton>

                <Divider orientation="vertical" flexItem />

                <IconButton
                    size="small"
                    title="Indent"
                    onMouseDown={(e) => { preventFocusLoss(e); handleExecCommand('indent'); }}
                >
                    <FormatIndentIncreaseIcon fontSize="small" />
                </IconButton>

                <IconButton
                    size="small"
                    title="Outdent"
                    onMouseDown={(e) => { preventFocusLoss(e); handleExecCommand('outdent'); }}
                >
                    <FormatIndentDecreaseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* ── Editable area ──
                  dir="ltr" HTML attribute  → forces LTR text direction, overrides
                                             the browser's bidi algorithm.
                  direction / unicodeBidi   → CSS-level LTR as a second layer.      */}
            <Box
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                dir="ltr"
                onFocus={handleFocus}
                onBlur={handleBlur}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                sx={{
                    p: 2,
                    minHeight,
                    maxHeight,
                    overflowY: 'auto',
                    fontSize: '0.95rem',
                    fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    color: '#333',
                    outline: 'none',
                    lineHeight: 1.6,

                    // RTL fix
                    direction: 'ltr',
                    unicodeBidi: 'normal',
                    textAlign: 'left',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',

                    '& ul': { paddingLeft: '20px', listStyleType: 'disc' },
                    '& ol': { paddingLeft: '20px', listStyleType: 'decimal' },
                    '& li': { marginBottom: '4px' },
                    '& b, & strong': { fontWeight: 700 },
                    '& i, & em': { fontStyle: 'italic' },
                    '& u': { textDecoration: 'underline' },

                    '&:empty:before': {
                        content: `"${placeholder}"`,
                        color: '#888888',
                        display: 'block',
                        opacity: 1,
                    },

                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#CFCFCF',
                        borderRadius: '6px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: '#AFAFAF',
                    },
                }}
            />
        </Box>
    );
};

export default CustomEditor;
